import { useState, useEffect, useMemo } from 'react';
import { Header } from '../components/Layout';
import { useParks } from '../contexts/ParksContext';
import {
  Sun, Battery, Zap, X, ArrowUp, ArrowDown,
  Activity, Cpu, Wifi, WifiOff, RefreshCw, AlertTriangle,
  ToggleLeft, ToggleRight
} from 'lucide-react';

// =====================================================================
// Topoloogia vaade — 15 päikeseparki kompaktses 5x3 grid'is.
// Klõps kaardile avab parempoolse slide-in paneeli kus on
// täielik info: aku, päikesejaam, võrk, PCS detailid, raw registrid.
// Eeskuju: parks_dashboard.py — sama värvi paletti ja struktuuri.
// =====================================================================

// ---------- Huawei status dekooder (vt huawei_sun2000.py STATUS_MAP) ----------
const HUAWEI_STATUS = {
  0x0000: 'Standby — initializing',
  0x0001: 'Standby — DC building',
  0x0002: 'Standby — grid detecting',
  0x0100: 'Starting',
  0x0200: 'On-grid',
  0x0201: 'On-grid (limited)',
  0x0202: 'On-grid (self derate)',
  0x0300: 'Shutdown — fault',
  0x0301: 'Shutdown — command',
  0x0302: 'Shutdown — OVGR',
  0x0303: 'Shutdown — comm disconnected',
  0x0304: 'Shutdown — power limit',
  0x0305: 'Shutdown — manual startup required',
  0x0306: 'Shutdown — DC switch off',
  0x0307: 'Shutdown — rapid shutdown',
  0x0308: 'Shutdown — input underpower',
  0x0401: 'Grid scheduling — cosphi-P',
  0x0402: 'Grid scheduling — Q-U',
  0x0500: 'Spot-check ready',
  0x0501: 'Spot-checking',
  0x0600: 'Inspecting',
  0x0700: 'AFCI self-check',
  0x0800: 'I-V scanning',
  0x0900: 'DC input detection',
  0x0a00: 'Off-grid charging',
  0xa000: 'Standby — no irradiation',
};
function decodeHuaweiStatus(code) {
  if (code == null) return '—';
  return HUAWEI_STATUS[code] || `Unknown (0x${code.toString(16).padStart(4, '0')})`;
}

// ---------- Numbri vorming (et-EE) ----------
function fmt(v, decimals) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  if (decimals === undefined) {
    const a = Math.abs(v);
    decimals = a >= 100 ? 1 : a >= 10 ? 2 : 3;
  }
  return v.toLocaleString('et-EE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function fmtKWh(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  if (v >= 1000) return fmt(v / 1000, 2) + ' MWh';
  return fmt(v, 1) + ' kWh';
}

// PCS olekukoodid (Modbus reg 229)
const PCS_STATUS = {
  0:  { label: 'Väljas',     tone: 'standby' },
  1:  { label: 'Self-Check', tone: 'standby' },
  2:  { label: 'Töötab',     tone: 'charge'  },
  4:  { label: 'Viga',       tone: 'fault'   },
  7:  { label: 'Standby',    tone: 'standby' },
  10: { label: 'Laeb',       tone: 'charge'  },
  11: { label: 'Tühjeneb',   tone: 'discharge' },
};

// =====================================================================
// PEAKOMPONENT
// =====================================================================
export function DevicesPage({ wsConnected }) {
  // Globaalne shared state ParksContext'ist — pole eraldi pollingu, lehte
  // vahetades andmed säilivad ja backend pollib taustal.
  const { data: ctxData, error: ctxError, refresh: fetchParks } = useParks();

  const updated = ctxData?.updated || null;
  const lastError = ctxError;

  const [selected, setSelected] = useState(null); // valitud park host'i järgi

  // Ainult aktori kliendi pargid (backend scope'ib), slot-järjekorras
  const gridParks = useMemo(
    () => (ctxData?.parks || []).slice().sort((a, b) => a.slot - b.slot),
    [ctxData],
  );

  const selectedPark = selected
    ? gridParks.find((p) => p.host === selected) || null
    : null;

  // ESC sulgeb paneeli
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') setSelected(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const onlineCount = gridParks.filter((p) => p.online).length;
  const installedCount = gridParks.filter((p) => p.name).length;
  const batteryCount = gridParks.filter((p) => p.pcs_online || p.bau_online).length;

  return (
    <div className="h-full flex flex-col" style={{ background: '#f5f7fb' }}>
      <Header
        title="Topoloogia"
        connected={wsConnected}
        onRefresh={fetchParks}
      />

      {/* Lokaalne stiil — index.css'i ei puutu (agent B teostab) */}
      <style>{topologyStyles}</style>

      <div className="topology-wrap">
        {/* Statistika riba */}
        <div className="topo-stats">
          <div className="topo-stat">
            <div className="topo-stat-label">Kokku parke</div>
            <div className="topo-stat-value">{installedCount}<span className="topo-stat-unit">/ {gridParks.length}</span></div>
          </div>
          <div className="topo-stat topo-stat-online">
            <div className="topo-stat-label">Online</div>
            <div className="topo-stat-value">{onlineCount}</div>
          </div>
          <div className="topo-stat topo-stat-battery">
            <div className="topo-stat-label">Akusüsteeme</div>
            <div className="topo-stat-value">{batteryCount}</div>
          </div>
          <div className="topo-stat topo-stat-meta">
            <div className="topo-stat-label">Andmeallikas</div>
            <div className="topo-stat-meta-value">
              {lastError ? (
                <span className="topo-pill topo-pill-warn">
                  <AlertTriangle size={12} /> /api/parks ei vasta
                </span>
              ) : (
                <span className="topo-pill topo-pill-ok">
                  <Activity size={12} /> /api/parks
                </span>
              )}
              {updated && (
                <span className="topo-stat-time">
                  {new Date(updated).toLocaleTimeString('et-EE')}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 5 x 3 grid — 15 parki */}
        <div className="topo-grid">
          {gridParks.map((p) => (
            <ParkCard
              key={p.slot}
              park={p}
              active={selectedPark?.host === p.host}
              onClick={() => p.name && setSelected(p.host)}
            />
          ))}
        </div>

        {ctxData && gridParks.length === 0 && (
          <div className="text-[13px] text-zinc-500 py-6">Selle kliendi all pole veel ühtegi parki.</div>
        )}
        {lastError && (
          <div className="topo-error-hint">
            <AlertTriangle size={14} />
            /api/parks ei vasta ({lastError})
          </div>
        )}
      </div>

      {/* Slide-in paremalt */}
      {selectedPark && (
        <ParkDetailPanel
          park={selectedPark}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

// =====================================================================
// PARK CARD — üks kompaktne kaart grid'is
// =====================================================================
function ParkCard({ park, active, onClick }) {
  const installed = !!park.name;
  const online = !!park.online;
  const hasBattery = !!(park.pcs_online || park.bau_online);

  // Placeholder kaart (paigaldamata)
  if (!installed) {
    return (
      <div className="topo-card topo-card-placeholder">
        <div className="topo-card-slot">SLOT {park.slot}</div>
        <div className="topo-card-host">{park.host}</div>
        <div className="topo-card-empty">Pole paigaldatud</div>
      </div>
    );
  }

  // Olek-värv
  const stateClass = online
    ? hasBattery ? 'topo-card-battery' : 'topo-card-online'
    : 'topo-card-offline';

  const pSolar = park.solar?.p_total;
  const pGrid = park.grid?.p_total;
  const exporting = pGrid !== null && pGrid !== undefined && pGrid < 0;
  // SoC fallback: kui PCS pollimine välja (Markedroid) või PCS bms_soc puudub,
  // kasuta BAU otsest soc-i. ParkCard kasutab sama eelistust.
  const soc = park.pcs?.bms_soc ?? park.bau?.soc ?? null;
  const pBatt = park.pcs?.p_total;
  const pcsState = park.pcs?.pcs_status !== undefined
    ? PCS_STATUS[park.pcs.pcs_status] || { label: '?', tone: 'standby' }
    : null;

  return (
    <button
      type="button"
      className={`topo-card ${stateClass} ${active ? 'topo-card-active' : ''}`}
      onClick={onClick}
    >
      <div className="topo-card-head">
        <div className="topo-card-title">
          <span className="topo-card-name">{park.name}</span>
          <span className="topo-card-slot-tag">#{park.slot}</span>
        </div>
        <span className={`topo-pill ${online ? 'topo-pill-ok' : 'topo-pill-off'}`}>
          <span className="topo-pill-dot" />
          {online ? 'Online' : 'Offline'}
        </span>
      </div>

      <div className="topo-card-host">{park.host}</div>

      {online ? (
        <>
          <div className="topo-card-metrics">
            <div className="topo-metric topo-metric-solar">
              <Sun size={14} />
              <div>
                <div className="topo-metric-label">Tootmine</div>
                <div className="topo-metric-value">{fmt(pSolar, 2)}<span>kW</span></div>
              </div>
            </div>
            <div className={`topo-metric ${exporting ? 'topo-metric-export' : 'topo-metric-import'}`}>
              {exporting ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
              <div>
                <div className="topo-metric-label">{exporting ? 'Võrku' : 'Võrgust'}</div>
                <div className="topo-metric-value">{fmt(Math.abs(pGrid || 0), 2)}<span>kW</span></div>
              </div>
            </div>
          </div>

          {hasBattery && (
            <div className="topo-card-battery-block">
              <div className="topo-card-battery-head">
                <span className="topo-card-battery-label">
                  <Battery size={12} /> Aku{park.batteries?.length ? ` ×${park.batteries.length + 1}` : ''}
                </span>
                <span className="topo-card-battery-soc">{fmt(soc, 1)}%</span>
              </div>
              <div className="topo-soc-bar">
                <div
                  className="topo-soc-bar-fill"
                  style={{ width: `${Math.max(0, Math.min(100, soc || 0))}%` }}
                />
              </div>
              <div className="topo-card-battery-foot">
                {pcsState && (
                  <span className={`topo-batt-stat topo-batt-${pcsState.tone}`}>
                    <span className="topo-pill-dot" />
                    {pcsState.label}
                  </span>
                )}
                <span className="topo-card-batt-power">
                  {fmt(Math.abs(pBatt || 0), 2)} kW
                </span>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="topo-card-offline-msg">
          <WifiOff size={14} />
          Gateway ei vasta
        </div>
      )}
    </button>
  );
}

// =====================================================================
// DETAIL PANEL — slide-in paremalt (täisinfo)
// =====================================================================
function ParkDetailPanel({ park, onClose }) {
  const [showRaw, setShowRaw] = useState(false);

  const pcsState = park.pcs?.pcs_status !== undefined
    ? PCS_STATUS[park.pcs.pcs_status] || { label: '?', tone: 'standby' }
    : null;

  const pBatt = park.pcs?.p_total;
  const charging = pBatt !== null && pBatt !== undefined && pBatt > 0.1;
  const discharging = pBatt !== null && pBatt !== undefined && pBatt < -0.1;
  const battTone = charging ? 'charge' : discharging ? 'discharge' : 'standby';
  const battStateLabel = charging ? 'Laeb' : discharging ? 'Tühjeneb' : 'Ootel';

  // SoC fallback BAU-le (vt eelnevat — kui PCS pollimine välja / Markedroid)
  const soc = park.pcs?.bms_soc ?? park.bau?.soc ?? null;
  const socClamped = Math.max(0, Math.min(100, soc || 0));

  return (
    <>
      <div className="topo-overlay" onClick={onClose} />
      <aside className="topo-panel" role="dialog" aria-label={`${park.name} detailid`}>
        <div className="topo-panel-head">
          <div>
            <div className="topo-panel-title">{park.name}</div>
            <div className="topo-panel-sub">
              <span className="topo-panel-host">{park.host}</span>
              <span className="topo-panel-sep">·</span>
              <span>Slot {park.slot}</span>
              {park.pcs_host && (
                <>
                  <span className="topo-panel-sep">·</span>
                  <span>PCS @ {park.pcs_host}</span>
                </>
              )}
            </div>
          </div>
          <button className="topo-panel-close" onClick={onClose} aria-label="Sulge">
            <X size={18} />
          </button>
        </div>

        <div className="topo-panel-body">
          {park.meter_protocol === 'deye_v5' ? (
            <DeyeSections park={park} showRaw={showRaw} setShowRaw={setShowRaw} />
          ) : (<>
          {/* PCS — pinge muundamise süsteem */}
          {park.pcs_online && park.pcs && (
            <section className="topo-section topo-section-battery">
              <header className="topo-section-head">
                <div className="topo-section-title">
                  <Cpu size={14} />
                  PCS
                  <span className="topo-section-tag">@ {park.pcs_host}</span>
                </div>
                {pcsState && (
                  <span className={`topo-batt-stat topo-batt-${battTone}`}>
                    <span className="topo-pill-dot" />
                    {pcsState.label}
                  </span>
                )}
              </header>

              <DetailGrid items={[
                ['AC võimsus',  fmt(pBatt, 2),                'kW'],
                ['DC võimsus',  fmt(park.pcs.dc_power, 2),    'kW'],
                ['DC pinge',    fmt(park.pcs.dc_voltage, 1),  'V'],
                ['DC vool',     fmt(park.pcs.dc_current, 2),  'A'],
                ['AC L1',       fmt(park.pcs.v_l1, 1),        'V'],
                ['AC L2',       fmt(park.pcs.v_l2, 1),        'V'],
                ['AC L3',       fmt(park.pcs.v_l3, 1),        'V'],
                ['Sagedus',     fmt(park.pcs.hz, 2),          'Hz'],
              ]} />
            </section>
          )}

          {/* BAU — Battery Active Unit (otse BMS @ bau_host:12230) */}
          {park.bau_online && park.bau && (
            <section className="topo-section topo-section-battery">
              <header className="topo-section-head">
                <div className="topo-section-title">
                  <Battery size={14} />
                  BAU
                  <span className="topo-section-tag">@ {park.bau_host}</span>
                </div>
                <span className="topo-section-power">SOC {fmt(park.bau.soc, 1)}%</span>
              </header>

              <div className="topo-soc-big">
                <div className="topo-soc-big-fill" style={{ width: `${Math.max(0, Math.min(100, park.bau.soc || 0))}%` }} />
              </div>

              <DetailGrid items={[
                ['Kogupinge',         fmt(park.bau.bank_voltage, 1),                   'V'],
                ['Kogu vool',         fmt(park.bau.bank_current, 1),                   'A'],
                ['SOC',               fmt(park.bau.soc, 1),                            '%'],
                ['SOH',               fmt(park.bau.soh, 1),                            '%'],
                ['Cell max V',        fmt(park.bau.vcell_max / 1000, 3),               'V'],
                ['Cell min V',        fmt(park.bau.vcell_min / 1000, 3),               'V'],
                ['Cell avg V',        fmt(park.bau.vcell_avg / 1000, 3),               'V'],
                ['Cell max T',        fmt(park.bau.tcell_max, 1),                      '°C'],
                ['Cell min T',        fmt(park.bau.tcell_min, 1),                      '°C'],
                ['Cell avg T',        fmt(park.bau.tcell_avg, 1),                      '°C'],
                ['Lub. chg I',        fmt(park.bau.current_chg_limit, 0),              'A'],
                ['Lub. dsg I',        fmt(park.bau.current_dsg_limit, 0),              'A'],
                ['Chg saadav',        fmt(park.bau.energy_chg_available, 0),           'kWh'],
                ['Dsg saadav',        fmt(park.bau.energy_dsg_available, 0),           'kWh'],
                ['Kogu charge',       fmt(park.bau.energy_chg_total, 0),               'kWh'],
                ['Kogu discharge',    fmt(park.bau.energy_dsg_total, 0),               'kWh'],
                ['Insulation',        fmt(park.bau.insulation_resistance, 0),          'kΩ'],
              ]} />

              {/* Aktiivsed alarmid (eestikeelsete nimedega, mitte hex) */}
              {Array.isArray(park.bau.active_alarms) && park.bau.active_alarms.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', fontWeight: 500, marginBottom: 6 }}>
                    Aktiivsed alarmid ({park.bau.active_alarms.length})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {park.bau.active_alarms.map((a, i) => {
                      const color = a.severity === 'critical' ? '#b91c1c' : a.severity === 'warning' ? '#b45309' : '#1d4ed8';
                      const bg   = a.severity === 'critical' ? 'rgba(220,38,38,0.06)' : a.severity === 'warning' ? 'rgba(180,83,9,0.06)' : 'rgba(29,78,216,0.05)';
                      const tag  = a.source === 'fault' ? 'FAULT' : `L${a.level}`;
                      return (
                        <div key={i} style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '4px 8px', background: bg, borderRadius: 3,
                          fontSize: '0.7rem', color,
                        }}>
                          <span>{a.name}</span>
                          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', opacity: 0.7 }}>
                            {tag} · bit {a.bit}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Täiendavad akud (battery #2+) — iga aku PCS + BAU põhinäidud.
              park.batteries[] tuleb ParksService._buildParkFromInflux'ist. */}
          {Array.isArray(park.batteries) && park.batteries.map((b) => {
            const bLabel = b.label || `Aku #${b.battery}`;
            const bPcsState = b.pcs?.pcs_status !== undefined
              ? PCS_STATUS[b.pcs.pcs_status] || { label: '?', tone: 'standby' }
              : null;
            const bPbatt = b.pcs?.p_total;
            const bCharging = bPbatt != null && bPbatt > 0.1;
            const bDischarging = bPbatt != null && bPbatt < -0.1;
            const bBattTone = bCharging ? 'charge' : bDischarging ? 'discharge' : 'standby';
            return (
              <div key={`bat-${b.battery}`} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Aku eraldusriba */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: '0.7rem', fontWeight: 600, color: '#6d28d9',
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                  paddingTop: 4, borderTop: '2px solid #ede9fe',
                }}>
                  <Battery size={14} /> {bLabel}
                  {b.pcs_host && <span style={{ fontSize: '0.58rem', fontWeight: 400, color: '#a1a1aa', textTransform: 'none', letterSpacing: 0 }}>PCS @ {b.pcs_host}</span>}
                  {!b.bau_online && <span style={{ fontSize: '0.58rem', fontWeight: 500, color: '#b91c1c', textTransform: 'none', letterSpacing: 0 }}>· BAU offline</span>}
                </div>

                {/* PCS */}
                {b.pcs_online && b.pcs && (
                  <section className="topo-section topo-section-battery">
                    <header className="topo-section-head">
                      <div className="topo-section-title">
                        <Cpu size={14} /> PCS
                        <span className="topo-section-tag">@ {b.pcs_host}</span>
                      </div>
                      {bPcsState && (
                        <span className={`topo-batt-stat topo-batt-${bBattTone}`}>
                          <span className="topo-pill-dot" /> {bPcsState.label}
                        </span>
                      )}
                    </header>
                    <DetailGrid items={[
                      ['AC võimsus',  fmt(bPbatt, 2),               'kW'],
                      ['DC võimsus',  fmt(b.pcs.dc_power, 2),       'kW'],
                      ['DC pinge',    fmt(b.pcs.dc_voltage, 1),     'V'],
                      ['DC vool',     fmt(b.pcs.dc_current, 2),     'A'],
                      ['AC L1',       fmt(b.pcs.v_l1, 1),           'V'],
                      ['AC L2',       fmt(b.pcs.v_l2, 1),           'V'],
                      ['AC L3',       fmt(b.pcs.v_l3, 1),           'V'],
                      ['Sagedus',     fmt(b.pcs.hz, 2),             'Hz'],
                    ]} />
                  </section>
                )}

                {/* BAU */}
                {b.bau_online && b.bau && (
                  <section className="topo-section topo-section-battery">
                    <header className="topo-section-head">
                      <div className="topo-section-title">
                        <Battery size={14} /> BAU
                        <span className="topo-section-tag">@ {b.bau_host}</span>
                      </div>
                      <span className="topo-section-power">SOC {fmt(b.bau.soc, 1)}%</span>
                    </header>

                    <div className="topo-soc-big">
                      <div className="topo-soc-big-fill" style={{ width: `${Math.max(0, Math.min(100, b.bau.soc || 0))}%` }} />
                    </div>

                    <DetailGrid items={[
                      ['Kogupinge',      fmt(b.bau.bank_voltage, 1),          'V'],
                      ['Kogu vool',      fmt(b.bau.bank_current, 1),          'A'],
                      ['SOC',            fmt(b.bau.soc, 1),                   '%'],
                      ['SOH',            fmt(b.bau.soh, 1),                   '%'],
                      ['Cell max V',     fmt(b.bau.vcell_max / 1000, 3),      'V'],
                      ['Cell min V',     fmt(b.bau.vcell_min / 1000, 3),      'V'],
                      ['Cell avg V',     fmt(b.bau.vcell_avg / 1000, 3),      'V'],
                      ['Cell max T',     fmt(b.bau.tcell_max, 1),             '°C'],
                      ['Cell min T',     fmt(b.bau.tcell_min, 1),             '°C'],
                      ['Cell avg T',     fmt(b.bau.tcell_avg, 1),             '°C'],
                      ['Lub. chg I',     fmt(b.bau.current_chg_limit, 0),     'A'],
                      ['Lub. dsg I',     fmt(b.bau.current_dsg_limit, 0),     'A'],
                      ['Kogu charge',    fmt(b.bau.energy_chg_total, 0),      'kWh'],
                      ['Kogu discharge', fmt(b.bau.energy_dsg_total, 0),      'kWh'],
                    ]} />

                    {/* Aktiivsed alarmid */}
                    {Array.isArray(b.bau.active_alarms) && b.bau.active_alarms.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', fontWeight: 500, marginBottom: 6 }}>
                          Aktiivsed alarmid ({b.bau.active_alarms.length})
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {b.bau.active_alarms.map((a, i) => {
                            const color = a.severity === 'critical' ? '#b91c1c' : a.severity === 'warning' ? '#b45309' : '#1d4ed8';
                            const bg   = a.severity === 'critical' ? 'rgba(220,38,38,0.06)' : a.severity === 'warning' ? 'rgba(180,83,9,0.06)' : 'rgba(29,78,216,0.05)';
                            const tag  = a.source === 'fault' ? 'FAULT' : `L${a.level}`;
                            return (
                              <div key={i} style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                padding: '4px 8px', background: bg, borderRadius: 3,
                                fontSize: '0.7rem', color,
                              }}>
                                <span>{a.name}</span>
                                <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', opacity: 0.7 }}>
                                  {tag} · bit {a.bit}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </section>
                )}
              </div>
            );
          })}

          {/* BAU energy meter (PowerEnv slave 0x7F) — CT 300:5 ratio rakendatud koodis */}
          {park.bau_online && park.bau && park.bau.meter_voltage_a !== null && (
            <section className="topo-section">
              <header className="topo-section-head">
                <div className="topo-section-title">
                  <Activity size={14} />
                  BAU Energy Meter
                  <span className="topo-section-tag">PowerEnv @ {park.bau_host} · CT 300:5</span>
                </div>
                <span className="topo-section-power">{fmt(park.bau.meter_power_total, 0)} W</span>
              </header>

              <DetailGrid items={[
                ['L1 pinge',     fmt(park.bau.meter_voltage_a, 1),     'V'],
                ['L2 pinge',     fmt(park.bau.meter_voltage_b, 1),     'V'],
                ['L3 pinge',     fmt(park.bau.meter_voltage_c, 1),     'V'],
                ['Sagedus',      fmt(park.bau.meter_frequency, 2),     'Hz'],
                ['L1 vool',      fmt(park.bau.meter_current_a, 2),     'A'],
                ['L2 vool',      fmt(park.bau.meter_current_b, 2),     'A'],
                ['L3 vool',      fmt(park.bau.meter_current_c, 2),     'A'],
                ['Power factor', fmt(park.bau.meter_pf, 3),            ''],
                ['Võimsus kokku',fmt(park.bau.meter_power_total, 0),   'W'],
                ['L1 võimsus',   fmt(park.bau.meter_power_a, 3),       'kW'],
                ['L2 võimsus',   fmt(park.bau.meter_power_b, 3),       'kW'],
                ['L3 võimsus',   fmt(park.bau.meter_power_c, 3),       'kW'],
                ['Reaktiiv',     fmt(park.bau.meter_reactive_power, 3),'kvar'],
                ['Energia kokku',fmt(park.bau.meter_energy_fwd, 0),    'kWh'],
                ['Sisetemp',     fmt(park.bau.meter_temperature, 1),   '°C'],
                ['Sisuniiskus',  fmt(park.bau.meter_humidity, 1),      '%RH'],
              ]} />
            </section>
          )}

          {/* Vedelikjahutus */}
          {park.bau_online && park.bau && (
            <section className="topo-section">
              <header className="topo-section-head">
                <div className="topo-section-title">
                  <Activity size={14} />
                  Vedelikjahutus
                  <span className="topo-section-tag">
                    {park.bau.lc_system_on_off ? 'Sees' : 'Väljas'}
                    {park.bau.liquid_cooling_comm_fault ? ' · COMM FAULT' : ''}
                  </span>
                </div>
                <span className="topo-section-power" style={{ color: park.bau.lc_fault_count > 0 ? '#b91c1c' : '#15803d' }}>
                  {park.bau.lc_fault_count > 0
                    ? `${park.bau.lc_fault_count} viga`
                    : 'OK'}
                </span>
              </header>

              {/* Aktiivsete vigade nimekiri (kui on) */}
              {Array.isArray(park.bau.lc_active_faults) && park.bau.lc_active_faults.length > 0 && (
                <div style={{
                  marginBottom: 12, padding: 10,
                  background: 'rgba(220,38,38,0.06)',
                  border: '1px solid rgba(220,38,38,0.18)',
                  borderRadius: 4,
                }}>
                  <div style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#b91c1c', fontWeight: 600, marginBottom: 6 }}>
                    Aktiivsed vead
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {park.bau.lc_active_faults.map((f, i) => (
                      <div key={i} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        fontSize: '0.7rem', color: '#7f1d1d',
                      }}>
                        <span>{f.name}</span>
                        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', color: '#b91c1c', opacity: 0.7 }}>
                          {f.addr} = {f.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <DetailGrid items={[
                ['Süsteem',        park.bau.lc_unit_state ? 'Sees' : 'Väljas',                    ''],
                ['Pump',           park.bau.lc_pump_state ? 'Sees' : 'Väljas',                    ''],
                ['Pumba kiirus',   fmt(park.bau.lc_pump_speed, 0),                                '%'],
                ['Kompressor',     park.bau.lc_compressor_state ? 'Sees' : 'Väljas',              ''],
                ['Kompr. kiirus',  fmt(park.bau.lc_compressor_speed, 0),                          ''],
                ['Vent. kiirus',   fmt(park.bau.lc_external_fan_speed, 0),                       ''],
                ['Elektriküte',    park.bau.lc_electric_heating_state ? 'Sees' : 'Väljas',        ''],
                ['Režiim',         fmt(park.bau.lc_operational_mode, 0),                          ''],
                ['Effluent (välja)',fmt(park.bau.lc_effluent_temp, 1),                            '°C'],
                ['Backwater (sisse)',fmt(park.bau.lc_backwater_temp, 1),                          '°C'],
                ['Exhaust',        fmt(park.bau.lc_exhaust_temp, 1),                              '°C'],
                ['Ümbritsev',      fmt(park.bau.lc_ambient_temp, 1),                              '°C'],
                ['Effluent rõhk',  fmt(park.bau.lc_effluent_pressure, 2),                         'bar'],
                ['Backwater rõhk', fmt(park.bau.lc_backwater_pressure, 2),                        'bar'],
                ['Refrig setpoint',fmt(park.bau.lc_refrigeration_point, 1),                       '°C'],
                ['Heat setpoint',  fmt(park.bau.lc_heating_point, 1),                             '°C'],
              ]} />
            </section>
          )}

          {/* Sisekliima — Niiskus + Küte + Sisetemp */}
          {park.bau_online && park.bau && (
            <section className="topo-section">
              <header className="topo-section-head">
                <div className="topo-section-title">
                  <Activity size={14} />
                  Sisekliima
                  <span className="topo-section-tag">
                    {fmt(park.bau.meter_temperature, 1)}°C · {fmt(park.bau.meter_humidity, 0)}%RH
                  </span>
                </div>
                <span className="topo-section-power">
                  {park.bau.humTemp_comm_fault ? 'Andur viga' : 'OK'}
                </span>
              </header>
              <DetailGrid items={[
                ['Sisetemp',           fmt(park.bau.meter_temperature, 1),   '°C'],
                ['Niiskus',            fmt(park.bau.meter_humidity, 1),      '%RH'],
                ['BAU max temp',       fmt(park.bau.bms_max_temp_env, 1),    '°C'],
                ['BAU min temp',       fmt(park.bau.bms_min_temp_env, 1),    '°C'],
                ['Niiskus-eemaldaja',  park.bau.dehum_state ? 'Sees' : 'Väljas', ''],
                ['Niiskus auto/käsi',  park.bau.dehum_auto_mode ? 'Auto' : 'Käsi', ''],
                ['Niiskus start',      fmt(park.bau.dehum_humidity_start, 0), '%'],
                ['Niiskus stop',       fmt(park.bau.dehum_humidity_stop, 0),  '%'],
                ['Elektriküte',        park.bau.heat_state ? 'Sees' : 'Väljas', ''],
                ['Küte start',         fmt(park.bau.heat_start_value, 0),    '°C'],
              ]} />
            </section>
          )}

          {/* Suitsuandur + DI sisendid */}
          {park.bau_online && park.bau && (
            <section className="topo-section">
              <header className="topo-section-head">
                <div className="topo-section-title">
                  <AlertTriangle size={14} />
                  Ohutus & sisendid
                </div>
                <span className="topo-section-power" style={{ color: park.bau.tempAndSmokeAlarm ? '#b91c1c' : '#15803d' }}>
                  {park.bau.tempAndSmokeAlarm ? 'ALARM!' : 'OK'}
                </span>
              </header>
              <DetailGrid items={[
                ['Suitsu/temp alarm',  park.bau.tempAndSmokeAlarm ? 'AKTIIVNE' : 'OK', ''],
                ['DI 1H',              park.bau.di1H ? 'Sees' : 'Väljas',  ''],
                ['DI 7L',              park.bau.di7L ? 'Sees' : 'Väljas',  ''],
                ['DI 8L',              park.bau.di8L ? 'Sees' : 'Väljas',  ''],
                ['DI 9L',              park.bau.di9L ? 'Sees' : 'Väljas',  ''],
                ['DI 10L',             park.bau.di10L ? 'Sees' : 'Väljas', ''],
                ['DI 11L',             park.bau.di11L ? 'Sees' : 'Väljas', ''],
              ]} />
            </section>
          )}

          {/* Päikesejaam */}
          {park.solar && (
            <section className="topo-section topo-section-solar">
              <header className="topo-section-head">
                <div className="topo-section-title">
                  <Sun size={16} />
                  Päikesejaam <span className="topo-section-tag">Slave 1</span>
                </div>
                <span className="topo-section-power">
                  {fmt(park.solar.p_total, 2)} kW
                </span>
              </header>

              <DetailGrid items={[
                ['L1 pinge', fmt(park.solar.v_l1, 1),               'V'],
                ['L2 pinge', fmt(park.solar.v_l2, 1),               'V'],
                ['L3 pinge', fmt(park.solar.v_l3, 1),               'V'],
                ['L1 vool',  fmt(Math.abs(park.solar.i_l1 || 0), 2),'A'],
                ['L2 vool',  fmt(Math.abs(park.solar.i_l2 || 0), 2),'A'],
                ['L3 vool',  fmt(Math.abs(park.solar.i_l3 || 0), 2),'A'],
                ['Sagedus',  fmt(park.solar.hz, 2),                 'Hz'],
                ['PF',       fmt(park.solar.pf, 3),                 ''],
                ['S',        fmt(park.solar.s_total, 2),            'kVA'],
              ]} />

              <div className="topo-energy-row topo-energy-solar">
                <span>Toodetud kokku (E_fwd)</span>
                <strong>{fmtKWh(park.solar.e_fwd)}</strong>
              </div>
            </section>
          )}

          {/* Võrk */}
          {park.grid && (
            <section className="topo-section topo-section-grid">
              <header className="topo-section-head">
                <div className="topo-section-title">
                  <Zap size={16} />
                  Võrk <span className="topo-section-tag">Slave 2</span>
                </div>
                <span className="topo-section-power">
                  {(park.grid.p_total !== null && park.grid.p_total < 0) ? (
                    <span style={{ color: '#15803d' }}>
                      <ArrowUp size={12} style={{ verticalAlign: '-1px' }} />
                      {' '}{fmt(Math.abs(park.grid.p_total), 2)} kW
                    </span>
                  ) : (
                    <span style={{ color: '#dc2626' }}>
                      <ArrowDown size={12} style={{ verticalAlign: '-1px' }} />
                      {' '}{fmt(Math.abs(park.grid.p_total || 0), 2)} kW
                    </span>
                  )}
                </span>
              </header>

              <DetailGrid items={[
                ['L1 pinge', fmt(park.grid.v_l1, 1),               'V'],
                ['L2 pinge', fmt(park.grid.v_l2, 1),               'V'],
                ['L3 pinge', fmt(park.grid.v_l3, 1),               'V'],
                ['L1 vool',  fmt(Math.abs(park.grid.i_l1 || 0), 2),'A'],
                ['L2 vool',  fmt(Math.abs(park.grid.i_l2 || 0), 2),'A'],
                ['L3 vool',  fmt(Math.abs(park.grid.i_l3 || 0), 2),'A'],
                ['Sagedus',  fmt(park.grid.hz, 2),                 'Hz'],
                ['PF',       fmt(park.grid.pf, 3),                 ''],
                ['S',        fmt(park.grid.s_total, 2),            'kVA'],
              ]} />

              <div className="topo-energy-row topo-energy-export">
                <span>Eksporditud võrku (E_rev)</span>
                <strong>{fmtKWh(park.grid.e_rev)}</strong>
              </div>
              <div className="topo-energy-row topo-energy-import">
                <span>Imporditud võrgust (E_fwd)</span>
                <strong>{fmtKWh(park.grid.e_fwd)}</strong>
              </div>
            </section>
          )}

          {/* Inverterid (Goodwe, Huawei, jms) — pollitud GoodweInverterPoller'iga */}
          {Array.isArray(park.inverters) && park.inverters.length > 0 && park.inverters.map((inv, idx) => {
            const invOnline = inv._online === true || (inv._age_ms != null && inv._age_ms < 60000);
            const pacKw = inv.pac != null ? inv.pac / 1000 : null;
            const acV = inv.ac_voltage || [];
            const acI = inv.ac_current || [];
            const wmLabel = inv.work_mode === 0 ? 'Wait/self-test'
              : inv.work_mode === 1 ? 'Normal'
              : inv.work_mode === 2 ? 'Fault' : '—';
            const wmTone = inv.work_mode === 1 ? '#15803d'
              : inv.work_mode === 2 ? '#dc2626' : '#64748b';
            return (
              <section key={`inv-${idx}`} className="topo-section">
                <header className="topo-section-head">
                  <div className="topo-section-title">
                    <Zap size={16} />
                    Inverter · {inv.type}
                    <span className="topo-section-tag">@ {inv.host} slave {inv.slave_id}</span>
                    {inv.label && <span className="topo-section-tag" style={{ opacity: 0.6, marginLeft: 4 }}>{inv.label}</span>}
                  </div>
                  <span className="topo-section-power">
                    {invOnline ? (
                      <span style={{ color: wmTone }}>● {wmLabel}</span>
                    ) : (
                      <span style={{ color: '#dc2626' }}>● Offline</span>
                    )}
                    {pacKw != null && <span style={{ marginLeft: 12 }}>{fmt(pacKw, 2)} kW</span>}
                  </span>
                </header>

                <DetailGrid items={[
                  ['AC võimsus (Pac)', fmt(pacKw, 2),                 'kW'],
                  ['Reaktiivvõimsus',  inv.reactive_power != null ? fmt(inv.reactive_power / 1000, 2) : '—', 'kvar'],
                  ['Power factor',     fmt(inv.power_factor, 3),      ''],
                  ['Sagedus',          fmt(inv.grid_freq ?? inv.frequency, 2), 'Hz'],
                  ['Sisetemp',         fmt(inv.internal_temp, 1),     '°C'],
                  ['Isolatsioon',      fmt(inv.insulation_res, 2),    'MΩ'],
                  ['Töörežiim',        wmLabel,                        ''],
                  ['Status',           inv.device_status != null
                                          ? `0x${inv.device_status.toString(16).padStart(4, '0')} · ${decodeHuaweiStatus(inv.device_status)}`
                                          : (inv.work_mode != null ? wmLabel : '—'), ''],
                  ['Efektiivsus',      fmt(inv.efficiency, 2),        '%'],
                  ['Power Limit',      inv.power_limit_active ? 'AKTIIVNE' : 'OFF', ''],
                  ['Warning code',     fmt(inv.warning_code, 0),      ''],
                  ['Function bits',    inv.function_bits != null ? '0x' + inv.function_bits.toString(16).toUpperCase().padStart(4, '0') : '—', ''],
                  ['Error word',       inv.error_word ? '0x' + inv.error_word.toString(16).toUpperCase().padStart(8, '0') : '0x00000000', ''],
                  ['E-Day',            fmt(inv.e_day, 1),             'kWh'],
                  ['E-Total',          fmt(inv.e_total, 1),           'kWh'],
                ]} />

                {/* PV stringid */}
                {(inv.pv1?.v != null || inv.pv2?.v != null || inv.pv3?.v != null || inv.pv4?.v != null) && (
                  <>
                    <div style={{
                      fontSize: '0.65rem', textTransform: 'uppercase',
                      letterSpacing: '0.06em', color: '#64748b',
                      fontWeight: 600, marginTop: 12, marginBottom: 6,
                    }}>
                      PV stringid (DC sisendid)
                    </div>
                    <DetailGrid items={[1, 2, 3, 4].flatMap(n => {
                      const pv = inv[`pv${n}`];
                      if (!pv || (pv.v == null && pv.i == null)) return [];
                      const pvW = (pv.v ?? 0) * (pv.i ?? 0);
                      return [
                        [`PV${n} pinge`, fmt(pv.v, 0), 'V'],
                        [`PV${n} vool`,  fmt(pv.i, 1), 'A'],
                        [`PV${n} võimsus`, fmt(pvW, 0), 'W'],
                      ];
                    })} />
                  </>
                )}

                {/* AC L1/L2/L3 */}
                {acV.some(v => v != null) && (
                  <>
                    <div style={{
                      fontSize: '0.65rem', textTransform: 'uppercase',
                      letterSpacing: '0.06em', color: '#64748b',
                      fontWeight: 600, marginTop: 12, marginBottom: 6,
                    }}>
                      AC väljund (võrk)
                    </div>
                    <DetailGrid items={[
                      ['L1 pinge', fmt(acV[0], 1), 'V'],
                      ['L2 pinge', fmt(acV[1], 1), 'V'],
                      ['L3 pinge', fmt(acV[2], 1), 'V'],
                      ['L1 vool',  fmt(acI[0], 2), 'A'],
                      ['L2 vool',  fmt(acI[1], 2), 'A'],
                      ['L3 vool',  fmt(acI[2], 2), 'A'],
                    ]} />
                  </>
                )}

                <div className="topo-energy-row topo-energy-solar">
                  <span>Toodetud kokku (E_total)</span>
                  <strong>{fmtKWh(inv.e_total)}</strong>
                </div>
              </section>
            );
          })}

          {/* Modbus toorväärtused */}
          <section className="topo-section">
            <header className="topo-section-head">
              <div className="topo-section-title">
                <Cpu size={16} />
                Modbus toorväärtused
              </div>
              <button
                type="button"
                className="topo-toggle"
                onClick={() => setShowRaw((v) => !v)}
                aria-pressed={showRaw}
              >
                {showRaw ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                <span>{showRaw ? 'Peida' : 'Kuva'}</span>
              </button>
            </header>

            {showRaw && (
              <div className="topo-raw">
                <RawTable title="solar (slave 1, ex9EMS)" data={park.solar} />
                <RawTable title="grid (slave 2, ex9EMS)"  data={park.grid} />
                <RawTable title="pcs (192.168.4.11, FC04)" data={park.pcs} />
                <RawTable title="bau (192.168.4.21 + PowerEnv 0x7F)" data={park.bau} />
              </div>
            )}
          </section>
          </>)}
        </div>
      </aside>
    </>
  );
}

// =====================================================================
// Deye hübriid (Solarman V5) — ainult Deye enda andmed, mitte BAU/PCS/ex9EMS plokid.
// park.deye = DeyeInverterPoller väljad (W, kWh); bat_power + = tühjeneb, grid_p + = import.
// =====================================================================
const DEYE_STATE = {
  0: { label: 'Ootel',      tone: 'standby' },
  1: { label: 'Enesetest',  tone: 'standby' },
  2: { label: 'Normaalne',  tone: 'charge' },
  3: { label: 'Hoiatus',    tone: 'discharge' },
  4: { label: 'Rike',       tone: 'discharge' },
};

function DeyeSections({ park, showRaw, setShowRaw }) {
  const d = park.deye || {};
  const kw = (w, dec = 2) => fmt(w == null ? null : w / 1000, dec);
  const st = DEYE_STATE[d.state] || { label: d.state == null ? 'Andmed puuduvad' : `Olek ${d.state}`, tone: 'standby' };
  const batW = d.bat_power;
  const battLabel = batW > 50 ? 'Tühjeneb' : batW < -50 ? 'Laeb' : 'Ootel';
  const battTone = batW > 50 ? 'discharge' : batW < -50 ? 'charge' : 'standby';
  const pvW = (d.pv1_p ?? 0) + (d.pv2_p ?? 0);
  const gridLabel = d.grid_p == null ? '—' : d.grid_p < 0 ? `Võrku ${kw(-d.grid_p)} kW` : `Võrgust ${kw(d.grid_p)} kW`;
  const hex = (v) => `0x${Number(v || 0).toString(16)}`;

  return (
    <>
      <section className="topo-section">
        <header className="topo-section-head">
          <div className="topo-section-title">
            <Cpu size={14} />
            Deye inverter
            <span className="topo-section-tag">Solarman V5 @ {park.host}</span>
          </div>
          <span className={`topo-batt-stat topo-batt-${st.tone}`}>
            <span className="topo-pill-dot" />
            {st.label}
          </span>
        </header>
        <DetailGrid items={[
          ['Väljundvõimsus', kw(d.out_p),                              'kW'],
          ['Hoiatus',        d.alarm ? hex(d.alarm) : 'pole',            ''],
          ['DC temperatuur', fmt(d.temp_dc, 1),                          '°C'],
          ['AC temperatuur', fmt(d.temp_ac, 1),                          '°C'],
          ['Rike',           d.fault ? `${hex(d.fault_code_lo)} / ${hex(d.fault_code_hi)}` : 'pole', ''],
          ['Inverteri SN',   park._meta?.inverter_sn || '—',             ''],
        ]} />
      </section>

      <section className="topo-section topo-section-battery">
        <header className="topo-section-head">
          <div className="topo-section-title">
            <Battery size={14} />
            Aku
            <span className={`topo-batt-stat topo-batt-${battTone}`} style={{ marginLeft: 8 }}>
              <span className="topo-pill-dot" />
              {battLabel}
            </span>
          </div>
          <span className="topo-section-power">SOC {fmt(d.bat_soc, 0)}%</span>
        </header>
        <div className="topo-soc-big">
          <div className="topo-soc-big-fill" style={{ width: `${Math.max(0, Math.min(100, d.bat_soc || 0))}%` }} />
        </div>
        <DetailGrid items={[
          ['Võimsus',           kw(batW == null ? null : Math.abs(batW)), 'kW'],
          ['Pinge',             fmt(d.bat_voltage, 1),                 'V'],
          ['Temperatuur',       fmt(d.bat_temp, 1),                    '°C'],
          ['SOC',               fmt(d.bat_soc, 0),                     '%'],
          ['Laetud täna',       fmt(d.e_bat_chg_today, 1),             'kWh'],
          ['Tühjenenud täna',   fmt(d.e_bat_dsg_today, 1),             'kWh'],
          ['Laetud kokku',      fmt(d.e_bat_chg_total, 0),             'kWh'],
          ['Tühjenenud kokku',  fmt(d.e_bat_dsg_total, 0),             'kWh'],
        ]} />
      </section>

      <section className="topo-section topo-section-solar">
        <header className="topo-section-head">
          <div className="topo-section-title">
            <Sun size={16} />
            Päikesepaneelid
          </div>
          <span className="topo-section-power">{kw(pvW)} kW</span>
        </header>
        <DetailGrid items={[
          ['PV1 pinge',      fmt(d.pv1_v, 1),          'V'],
          ['PV2 pinge',      fmt(d.pv2_v, 1),          'V'],
          ['PV1 vool',       fmt(d.pv1_i, 1),          'A'],
          ['PV2 vool',       fmt(d.pv2_i, 1),          'A'],
          ['PV1 võimsus',    kw(d.pv1_p),              'kW'],
          ['PV2 võimsus',    kw(d.pv2_p),              'kW'],
          ['Toodang täna',   fmt(d.e_pv_today, 1),     'kWh'],
          ['Toodang kokku',  fmt(d.e_pv_total, 0),     'kWh'],
        ]} />
      </section>

      <section className="topo-section topo-section-grid">
        <header className="topo-section-head">
          <div className="topo-section-title">
            <Zap size={16} />
            Võrk
          </div>
          <span className="topo-section-power">{gridLabel}</span>
        </header>
        <DetailGrid items={[
          ['L1 pinge',       fmt(d.grid_v_l1, 1),       'V'],
          ['L1 võimsus',     kw(d.grid_p_l1),           'kW'],
          ['L2 pinge',       fmt(d.grid_v_l2, 1),       'V'],
          ['L2 võimsus',     kw(d.grid_p_l2),           'kW'],
          ['L3 pinge',       fmt(d.grid_v_l3, 1),       'V'],
          ['L3 võimsus',     kw(d.grid_p_l3),           'kW'],
          ['L1 vool',        fmt(d.grid_i_l1, 2),       'A'],
          ['L2 vool',        fmt(d.grid_i_l2, 2),       'A'],
          ['L3 vool',        fmt(d.grid_i_l3, 2),       'A'],
          ['Sagedus',        fmt(d.grid_hz, 2),         'Hz'],
          ['Võrgust täna',   fmt(d.e_import_today, 1),  'kWh'],
          ['Võrku täna',     fmt(d.e_export_today, 1),  'kWh'],
          ['Võrgust kokku',  fmt(d.e_import_total, 0),  'kWh'],
          ['Võrku kokku',    fmt(d.e_export_total, 0),  'kWh'],
        ]} />
      </section>

      <section className="topo-section">
        <header className="topo-section-head">
          <div className="topo-section-title">
            <Activity size={16} />
            Koormus
          </div>
          <span className="topo-section-power">{kw(d.load_p)} kW</span>
        </header>
        <DetailGrid items={[
          ['L1 võimsus',       kw(d.load_p_l1),          'kW'],
          ['L2 võimsus',       kw(d.load_p_l2),          'kW'],
          ['L3 võimsus',       kw(d.load_p_l3),          'kW'],
          ['Kokku',            kw(d.load_p),             'kW'],
          ['Tarbimine täna',   fmt(d.e_load_today, 1),   'kWh'],
          ['Tarbimine kokku',  fmt(d.e_load_total, 0),   'kWh'],
        ]} />
      </section>

      <section className="topo-section">
        <header className="topo-section-head">
          <div className="topo-section-title">
            <Cpu size={16} />
            Toorandmed
          </div>
          <button
            type="button"
            className="topo-toggle"
            onClick={() => setShowRaw((v) => !v)}
            aria-pressed={showRaw}
          >
            {showRaw ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
            <span>{showRaw ? 'Peida' : 'Kuva'}</span>
          </button>
        </header>
        {showRaw && (
          <div className="topo-raw">
            <RawTable title="deye (Solarman V5, registrid 500–694)" data={park.deye} />
          </div>
        )}
      </section>
    </>
  );
}

// =====================================================================
// Abikomponendid
// =====================================================================
function DetailGrid({ items }) {
  return (
    <div className="topo-detail-grid">
      {items.map(([label, value, unit], i) => (
        <div className="topo-detail-row" key={i}>
          <span className="topo-detail-key">{label}</span>
          <span className="topo-detail-val">
            {value}{unit ? <span className="topo-detail-unit"> {unit}</span> : null}
          </span>
        </div>
      ))}
    </div>
  );
}

function RawTable({ title, data }) {
  if (!data || typeof data !== 'object') {
    return (
      <div className="topo-raw-block">
        <div className="topo-raw-title">{title}</div>
        <div className="topo-raw-empty">— andmed puuduvad —</div>
      </div>
    );
  }
  const entries = Object.entries(data);
  return (
    <div className="topo-raw-block">
      <div className="topo-raw-title">{title}</div>
      <table className="topo-raw-table">
        <tbody>
          {entries.map(([k, v]) => (
            <tr key={k}>
              <td className="topo-raw-k">{k}</td>
              <td className="topo-raw-v">
                {v === null || v === undefined ? '—'
                  : typeof v === 'number' ? v.toLocaleString('et-EE', { maximumFractionDigits: 4 })
                  : String(v)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =====================================================================
// CSS — port'itud parks_dashboard.py'st (light theme).
// Hoidame kohaliku <style> tag'i sees, et mitte muuta index.css'i.
// =====================================================================
const topologyStyles = `
.topology-wrap {
  flex: 1;
  overflow: auto;
  padding: 24px 32px 48px;
  background: #f7f8fa;
  color: #18181b;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ---------- Stat riba (1px joonseparated) ---------- */
.topo-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1px;
  margin-bottom: 24px;
  background: #eaecef;
  border: 1px solid #eaecef;
  border-radius: 6px;
  overflow: hidden;
}
.topo-stat {
  background: #ffffff;
  padding: 12px 16px;
  position: relative;
}
.topo-stat::after { content: none; }
.topo-stat-label {
  font-size: 0.62rem; color: #71717a; text-transform: uppercase;
  letter-spacing: 0.06em; font-weight: 500; margin-bottom: 6px;
}
.topo-stat-value {
  font-size: 1.25rem; font-weight: 600; letter-spacing: -0.02em;
  color: #18181b; font-variant-numeric: tabular-nums; line-height: 1.1;
}
.topo-stat-online .topo-stat-value { color: #15803d; }
.topo-stat-battery .topo-stat-value { color: #6d28d9; }
.topo-stat-unit {
  font-size: 0.6em; color: #71717a; margin-left: 4px; font-weight: 500;
}
.topo-stat-meta-value {
  display: flex; flex-direction: column; gap: 4px;
  font-size: 0.7rem;
}
.topo-stat-time {
  color: #71717a; font-variant-numeric: tabular-nums; font-size: 0.65rem;
}

/* ---------- Pill ---------- */
.topo-pill {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 1px 6px; border-radius: 3px;
  font-size: 0.6rem; font-weight: 500; letter-spacing: 0.02em;
  background: transparent;
  border: 1px solid #eaecef;
}
.topo-pill-dot {
  width: 4px; height: 4px; border-radius: 50%; background: currentColor;
}
.topo-pill-ok { color: #15803d; }
.topo-pill-ok .topo-pill-dot {
  animation: topo-pulse 2s infinite;
}
.topo-pill-off { color: #a1a1aa; }
.topo-pill-warn { color: #b45309; }
@keyframes topo-pulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.35; }
}

/* ---------- 5 x 3 GRID ---------- */
.topo-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
}
@media (max-width: 1180px) {
  .topo-grid { grid-template-columns: repeat(3, 1fr); }
}
@media (max-width: 720px) {
  .topo-grid { grid-template-columns: repeat(2, 1fr); }
}

/* ---------- Kaart ---------- */
.topo-card {
  text-align: left; cursor: pointer; font: inherit;
  background: #ffffff;
  border: 1px solid #eaecef;
  border-radius: 6px;
  padding: 12px;
  display: flex; flex-direction: column; gap: 10px;
  transition: border-color 0.12s, background 0.12s;
  position: relative;
  min-height: 134px;
}
.topo-card:hover {
  border-color: #d6d9de;
}
.topo-card-active {
  border-color: #6d28d9 !important;
  background: #fafaff;
}
.topo-card-online::before {
  content: ""; position: absolute; left: 0; right: 0; top: 0; height: 1px;
  background: #15803d;
  opacity: 0.5;
}
.topo-card-battery::before {
  content: ""; position: absolute; left: 0; right: 0; top: 0; height: 1px;
  background: #6d28d9;
  opacity: 0.6;
}
.topo-card-offline {
  background: #fafbfc;
  opacity: 0.85;
}
.topo-card-placeholder {
  background: transparent;
  border-style: dashed;
  border-color: #d6d9de;
  cursor: default;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 4px; min-height: 134px;
  text-align: center;
}
.topo-card-slot {
  font-size: 0.6rem; font-weight: 500; color: #a1a1aa;
  letter-spacing: 0.06em; text-transform: uppercase;
}
.topo-card-empty {
  font-size: 0.7rem; color: #a1a1aa; font-weight: 400;
}

.topo-card-head {
  display: flex; justify-content: space-between; align-items: flex-start;
  gap: 8px;
}
.topo-card-title {
  display: flex; flex-direction: column; gap: 1px; min-width: 0;
}
.topo-card-name {
  font-size: 0.82rem; font-weight: 600; color: #18181b;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  letter-spacing: -0.005em;
}
.topo-card-slot-tag {
  font-size: 0.58rem; color: #a1a1aa; font-weight: 500;
  letter-spacing: 0.04em;
}
.topo-card-host {
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
  font-size: 0.62rem; color: #a1a1aa;
}

.topo-card-metrics {
  display: grid; grid-template-columns: 1fr 1fr; gap: 1px;
  margin-top: 2px;
  background: #f0f1f4;
  border-radius: 4px;
  overflow: hidden;
}
.topo-metric {
  display: flex; flex-direction: column; gap: 2px;
  padding: 6px 8px;
  background: #ffffff;
}
.topo-metric-solar .topo-metric-value { color: #ca8a04; }
.topo-metric-export .topo-metric-value { color: #15803d; }
.topo-metric-import .topo-metric-value { color: #b91c1c; }
.topo-metric-label {
  font-size: 0.55rem; color: #71717a; text-transform: uppercase;
  letter-spacing: 0.05em; font-weight: 500;
}
.topo-metric-value {
  font-size: 0.82rem; font-weight: 600; color: #18181b;
  font-variant-numeric: tabular-nums; letter-spacing: -0.01em;
}
.topo-metric-value span {
  font-size: 0.65em; color: #71717a; margin-left: 2px; font-weight: 400;
}

/* ---------- Kaardi aku-blokk ---------- */
.topo-card-battery-block {
  background: #fafbfc;
  border: 1px solid #eaecef;
  border-radius: 4px;
  padding: 8px 10px;
  display: flex; flex-direction: column; gap: 5px;
}
.topo-card-battery-head {
  display: flex; justify-content: space-between; align-items: center;
  font-size: 0.65rem;
}
.topo-card-battery-label {
  display: inline-flex; align-items: center; gap: 4px;
  color: #6d28d9; font-weight: 500; letter-spacing: 0.04em;
  text-transform: uppercase; font-size: 0.55rem;
}
.topo-card-battery-soc {
  font-weight: 600; color: #6d28d9;
  font-variant-numeric: tabular-nums;
  font-size: 0.78rem;
}
.topo-soc-bar {
  height: 4px; background: #f0f1f4;
  border-radius: 999px; overflow: hidden;
  border: none;
}
.topo-soc-bar-fill {
  height: 100%; border-radius: 999px;
  background: #6d28d9;
  transition: width 0.6s ease;
}
.topo-card-battery-foot {
  display: flex; justify-content: space-between; align-items: center;
  font-size: 0.65rem;
}
.topo-card-batt-power {
  font-variant-numeric: tabular-nums; color: #71717a; font-weight: 500;
}

.topo-batt-stat {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 1px 6px; border-radius: 3px;
  font-size: 0.58rem; font-weight: 500; letter-spacing: 0.02em;
  background: transparent;
  border: 1px solid #eaecef;
}
.topo-batt-charge { color: #15803d; }
.topo-batt-discharge { color: #b45309; }
.topo-batt-standby { color: #71717a; }
.topo-batt-fault { color: #b91c1c; }
.topo-batt-charge .topo-pill-dot,
.topo-batt-discharge .topo-pill-dot { animation: topo-pulse 2s infinite; }

.topo-card-offline-msg {
  display: flex; align-items: center; gap: 6px; justify-content: center;
  padding: 12px 8px;
  font-size: 0.65rem; color: #a1a1aa;
  background: transparent;
}

.topo-error-hint {
  margin-top: 16px;
  padding: 8px 12px;
  border-radius: 4px;
  background: transparent;
  border: 1px solid #eaecef;
  color: #b45309;
  font-size: 0.7rem;
  display: flex; align-items: center; gap: 6px;
}

/* ---------- Slide-in panel ---------- */
.topo-overlay {
  position: fixed; inset: 0; z-index: 60;
  background: rgba(15,23,42,0.18);
  animation: topo-fade 0.2s ease;
}
@keyframes topo-fade {
  from { opacity: 0; }
  to   { opacity: 1; }
}
.topo-panel {
  position: fixed; top: 0; right: 0; bottom: 0;
  width: min(560px, 100%);
  background: #ffffff;
  border-left: 1px solid #eaecef;
  box-shadow: -8px 0 24px rgba(15,23,42,0.06);
  z-index: 70;
  display: flex; flex-direction: column;
  animation: topo-slide 0.22s cubic-bezier(0.22, 1, 0.36, 1);
}
@keyframes topo-slide {
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
}
.topo-panel-head {
  padding: 16px 20px;
  border-bottom: 1px solid #eaecef;
  display: flex; justify-content: space-between; align-items: flex-start;
  gap: 12px;
  background: #ffffff;
}
.topo-panel-title {
  font-size: 0.92rem; font-weight: 600; color: #18181b;
  letter-spacing: -0.005em; margin-bottom: 2px;
}
.topo-panel-sub {
  font-size: 0.7rem; color: #71717a;
  display: flex; flex-wrap: wrap; gap: 6px; align-items: center;
}
.topo-panel-host {
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
  color: #71717a; font-weight: 400; font-size: 0.66rem;
}
.topo-panel-sep { color: #d4d4d8; }
.topo-panel-close {
  width: 26px; height: 26px; border-radius: 4px;
  border: 1px solid #eaecef;
  background: #ffffff; cursor: pointer;
  display: grid; place-items: center;
  color: #71717a;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.topo-panel-close:hover {
  background: #fafbfc; border-color: #d6d9de; color: #18181b;
}
.topo-panel-body {
  flex: 1; overflow-y: auto;
  padding: 16px 20px 24px;
  display: flex; flex-direction: column; gap: 14px;
}

/* Sektsioonid */
.topo-section {
  background: #ffffff;
  border: 1px solid #eaecef;
  border-radius: 6px;
  padding: 14px;
}
.topo-section-battery,
.topo-section-solar,
.topo-section-grid {
  background: #ffffff;
}
.topo-section-head {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 10px;
  gap: 10px; flex-wrap: wrap;
}
.topo-section-title {
  display: flex; align-items: center; gap: 6px;
  font-size: 0.65rem; font-weight: 500; color: #71717a;
  text-transform: uppercase; letter-spacing: 0.06em;
}
.topo-section-battery .topo-section-title { color: #6d28d9; }
.topo-section-solar .topo-section-title { color: #ca8a04; }
.topo-section-grid .topo-section-title { color: #1d4ed8; }
.topo-section-tag {
  font-size: 0.58rem; font-weight: 400; color: #a1a1aa;
  background: transparent; padding: 1px 6px; border-radius: 3px;
  border: 1px solid #eaecef;
  text-transform: none; letter-spacing: 0;
}
.topo-section-power {
  font-size: 0.85rem; font-weight: 600;
  font-variant-numeric: tabular-nums; color: #18181b;
}

/* SOC suur riba */
.topo-soc-big {
  height: 8px; border-radius: 999px; overflow: hidden;
  background: #f0f1f4;
  position: relative; margin-bottom: 12px;
}
.topo-soc-big-fill {
  height: 100%;
  background: #6d28d9;
  transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1);
}
.topo-soc-big-text {
  display: none;
}

/* Detail grid */
.topo-detail-grid {
  display: grid; grid-template-columns: repeat(2, 1fr);
  gap: 2px 16px;
}
.topo-detail-row {
  display: flex; justify-content: space-between; align-items: baseline;
  padding: 3px 0;
  border-bottom: 1px solid #f0f1f4;
  font-size: 0.72rem;
}
.topo-detail-key { color: #71717a; font-weight: 400; }
.topo-detail-val {
  color: #18181b;
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
  font-variant-numeric: tabular-nums; font-weight: 500;
  font-size: 0.92em;
}
.topo-detail-unit { color: #a1a1aa; font-weight: 400; }

/* Energia ridad */
.topo-energy-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 6px 10px; margin-top: 8px;
  border-radius: 3px;
  background: transparent;
  border: 1px solid #eaecef;
  font-size: 0.7rem;
  color: #71717a;
}
.topo-energy-row strong {
  font-family: 'JetBrains Mono', monospace;
  font-variant-numeric: tabular-nums; font-weight: 600;
  color: #18181b;
}
.topo-energy-solar strong { color: #ca8a04; }
.topo-energy-export strong { color: #15803d; }
.topo-energy-import strong { color: #b91c1c; }

/* Toggle ja raw */
.topo-toggle {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 8px; border-radius: 4px;
  background: #ffffff;
  border: 1px solid #d6d9de;
  color: #52525b; cursor: pointer;
  font-size: 0.66rem; font-weight: 500; font-family: inherit;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.topo-toggle:hover {
  background: #fafbfc; color: #18181b;
  border-color: #b4b8be;
}
.topo-toggle[aria-pressed="true"] {
  background: transparent;
  border-color: #6d28d9;
  color: #6d28d9;
}

.topo-raw {
  display: flex; flex-direction: column; gap: 8px;
  margin-top: 8px;
}
.topo-raw-block {
  background: #ffffff;
  border: 1px solid #eaecef;
  border-radius: 4px;
  overflow: hidden;
}
.topo-raw-title {
  font-size: 0.62rem; font-weight: 500; color: #71717a;
  text-transform: uppercase; letter-spacing: 0.06em;
  padding: 6px 10px; background: #fafbfc;
  border-bottom: 1px solid #eaecef;
}
.topo-raw-empty {
  padding: 10px; color: #a1a1aa; text-align: center;
  font-size: 0.7rem;
}
.topo-raw-table {
  width: 100%; border-collapse: collapse;
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
  font-size: 0.66rem;
}
.topo-raw-table tr { border-bottom: 1px solid #f0f1f4; }
.topo-raw-table tr:last-child { border-bottom: none; }
.topo-raw-table td {
  padding: 3px 10px;
}
.topo-raw-k { color: #71717a; width: 40%; }
.topo-raw-v {
  color: #18181b; text-align: right;
  font-variant-numeric: tabular-nums;
}

@media (max-width: 720px) {
  .topology-wrap { padding: 16px; }
  .topo-panel { width: 100%; }
  .topo-detail-grid { grid-template-columns: 1fr; }
  .topo-card-metrics { grid-template-columns: 1fr; }
}
`;

export default DevicesPage;
